"use client";

/**
 * The Line's hydration-safe reads for the /home door. Client-only: they sit
 * on useSyncExternalStore, which a server component may not import, and the
 * pure predicates (isDockVisible) stay in home-flags.ts for those pages.
 */
import { useSyncExternalStore } from "react";
import { isDesktopShell } from "@/lib/desktop-shell";
import {
  floatingDockOn,
  isDockWindow,
  subscribeFloatingDock,
} from "@/lib/dock-window";
import { isDockVisible } from "@/lib/home-flags";

const noSubscribe = () => () => {};

/**
 * The Line on this surface: on everywhere except inside the desktop shell,
 * whose native push-to-talk already owns Control (ptt.rs); two arms on one
 * key would open a billed session on every hold. Hydration-safe the way
 * presence-copy reads the shell: the server's answer is fixed, the client's
 * lands after hydration.
 * The one exception is the shell's own Dock window (/dock, spec 2026-09-22):
 * there the Line IS the voice, and Control is routed to it by the shell.
 */
export function useLineOn(): boolean {
  const inShell = useSyncExternalStore(
    noSubscribe,
    () => isDesktopShell() && !isDockWindow(),
    () => false
  );
  return !inShell;
}

/**
 * Whether the shell's answer above is the client's own: false on the server
 * and through the hydration commit (which still carries the server's), true
 * from the render that follows. An effect that acts on useLineOn (a focus, a
 * parked draft) waits for this so the hydration commit never acts on an
 * answer the next render takes back. A client render is settled at once.
 */
export function useShellSettled(): boolean {
  return useSyncExternalStore(
    noSubscribe,
    () => true,
    () => false
  );
}

/**
 * The shell's floating Dock is on and this is its MAIN window (dock-window.ts
 * floatingDockOn): the panel is the composer, so this window draws none. False
 * on the server and through hydration; the shell's answer from the render
 * after it lands.
 */
export function useFloatingDockOn(): boolean {
  return useSyncExternalStore(
    subscribeFloatingDock,
    () => floatingDockOn(),
    () => false
  );
}

/** isDockVisible for a component: the route's answer, and never while the
 *  floating Dock stands in for this window's own (whatever clears the dock,
 *  a toast, sits at the bottom then). */
export function useDockVisible(pathname: string | null | undefined): boolean {
  const floating = useFloatingDockOn();
  return isDockVisible(pathname) && !floating;
}
